import { useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { NewUploadDialog } from '../components/NewUploadDialog';
import { usePageTitle } from '../hooks/usePageTitle';
import styles from './AdminOrderListPage.module.css';

interface AdminOrder {
  order_id: string;
  shopify_order_number: string;
  customer_id: string;
  customer_name: string | null;
  customer_email: string | null;
  created_at: string;
  scan_count: number;
  scan_ready: boolean;
  fulfillment_status: string | null;
  retention_expires_at: string | null;
  expired: boolean;
  is_test?: boolean;
}

async function fetchAdminOrders(handleUnauthorized: () => void): Promise<AdminOrder[]> {
  const res = await fetch('/api/v1/admin/orders', { credentials: 'include' });
  if (res.status === 401 || res.status === 403) {
    handleUnauthorized();
    throw new Error('Unauthorized');
  }
  if (!res.ok) {
    throw new Error('Failed to fetch orders');
  }
  const data = await res.json();
  return data.orders;
}

export function AdminOrderListPage() {
  usePageTitle('Admin — Orders');
  const { handleUnauthorized, logout } = useAdminAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const showToast = useCallback((message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  }, []);

  const handleSyncOrders = useCallback(async () => {
    setIsSyncing(true);
    try {
      const res = await fetch('/api/v1/admin/orders/sync', {
        method: 'POST',
        credentials: 'include',
      });
      if (res.status === 202) {
        showToast('Sync started', 'success');
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: ['admin', 'orders'] });
          setIsSyncing(false);
        }, 10000);
      } else if (res.status === 401 || res.status === 403) {
        handleUnauthorized();
        setIsSyncing(false);
      } else {
        showToast('Failed to start sync. Please try again.', 'error');
        setIsSyncing(false);
      }
    } catch {
      showToast('Failed to start sync. Please try again.', 'error');
      setIsSyncing(false);
    }
  }, [queryClient, showToast, handleUnauthorized]);

  const { data: orders, isLoading, error } = useQuery({
    queryKey: ['admin', 'orders'],
    queryFn: () => fetchAdminOrders(handleUnauthorized),
  });

  return (
    <main className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.heading}>Admin — Orders</h1>
        <div className={styles.headerActions}>
          <button
            onClick={() => setDialogOpen(true)}
            className={styles.newUploadBtn}
          >
            New Upload
          </button>
          <button
            onClick={handleSyncOrders}
            disabled={isSyncing}
            className={styles.syncBtn}
          >
            {isSyncing ? (
              <>
                <span className={styles.spinner} aria-hidden="true" />
                Syncing...
              </>
            ) : (
              'Sync Orders'
            )}
          </button>
          <Link to="/admin/print-orders" className={styles.printOrdersLink}>Print Orders</Link>
          <button onClick={logout} className={styles.logoutBtn}>Logout</button>
        </div>
      </div>

      {isLoading && <p className={styles.loading}>Loading orders...</p>}
      {error && <p className={styles.error}>Error loading orders.</p>}

      {orders && orders.length > 0 && (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Order #</th>
                <th>Customer</th>
                <th>Customer Email</th>
                <th>Date</th>
                <th>Scans</th>
                <th>Status</th>
                <th>Fulfilled</th>
                <th>Retention Expires</th>
                <th>Expired</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <tr key={order.order_id}>
                  <td>
                    {order.shopify_order_number}
                    {order.is_test && <span className={styles.badgeTest}>TEST</span>}
                  </td>
                  <td>
                    {/* A nameless order shows an em dash, matching the email
                        column — falling back to customer_id leaked raw
                        `test-cust-…` UUIDs into the Customer column (test
                        orders created without a name; the copyable ID chip
                        below already covers the lookup need). */}
                    {order.customer_name || '—'}
                    {order.is_test && (
                      <>
                        <br />
                        <button
                          type="button"
                          className={styles.copyId}
                          title="Copy customer ID (for feature-flag allowlists)"
                          onClick={() => navigator.clipboard?.writeText(order.customer_id)}
                        >
                          {order.customer_id}
                        </button>
                      </>
                    )}
                  </td>
                  <td>{order.customer_email || '—'}</td>
                  <td>{new Date(order.created_at).toLocaleDateString()}</td>
                  <td>{order.scan_count}</td>
                  <td>
                    {order.scan_count === 0 ? (
                      <span className={styles.badgeAwaiting}>Awaiting Upload</span>
                    ) : (
                      <span className={styles.badgeReady}>Ready</span>
                    )}
                  </td>
                  <td>
                    {order.fulfillment_status === 'fulfilled' ? 'Yes' :
                     order.fulfillment_status === 'partial' ? 'Partial' : 'No'}
                  </td>
                  <td>
                    {order.retention_expires_at
                      ? new Date(order.retention_expires_at).toLocaleDateString()
                      : '—'}
                  </td>
                  <td>{order.expired ? 'Yes' : 'No'}</td>
                  <td>
                    {order.scan_count === 0 ? (
                      <Link to={`/admin/orders/${order.order_id}/upload`}>Upload Scans</Link>
                    ) : (
                      <Link to={`/admin/orders/${order.order_id}/upload`}>View</Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {orders && orders.length === 0 && <p className={styles.empty}>No orders found.</p>}

      <NewUploadDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onResolved={(orderId) => {
          setDialogOpen(false);
          navigate(`/admin/orders/${orderId}/upload`);
        }}
      />

      {toast && (
        <div
          className={`${styles.toast} ${toast.type === 'error' ? styles.toastError : styles.toastSuccess}`}
          role="status"
          aria-live="polite"
        >
          {toast.message}
        </div>
      )}
    </main>
  );
}
